/**
 * What the van prints: the same 80 mm tax-invoice receipt the counter
 * prints (built from the phone's own document and QR), and a collection
 * receipt for cash taken against a customer's bills.
 */
import type { SaleDetail } from '../api/types'
import { printHtml, receiptHtml } from '../inventory/receipt'
import { type Device, type VanDoc, priceLine, qrDataUrl } from './model'
import { loadPrinter, printBlocks } from './printer'
import { type Block, collectionTicket, saleTicket } from './ticket'

export async function saleDetailOf(d: Device, doc: VanDoc): Promise<SaleDetail> {
  const b = d.bootstrap
  const co = b.company
  const cust = doc.customer_code ? b.customers.find((c) => c.code === doc.customer_code) : null
  const lines = (doc.lines ?? []).map((l, i) => {
    const it = b.items.find((x) => x.code === l.item_code)!
    const p = priceLine(it, l)
    return { id: i + 1, line_no: i + 1, item_code: it.code, item_name: it.name_en,
             item_name_ar: it.name_ar, unit: l.unit, factor: '1', qty: l.qty, price: l.price,
             discount: l.discount || '0.00', net: p.net, vat_rate: it.vat_rate,
             vat_amount: p.vat, total: p.total, returned: '0', returnable: '0',
             price_override: false }
  })
  const vat = doc.customer_vat || cust?.vat_number || null
  return {
    id: 0, uid: doc.uid, kind: doc.kind === 'RETURN' ? 'RETURN' : 'INVOICE',
    invoice_type: vat ? 'STANDARD' : 'SIMPLIFIED', doc_no: doc.doc_no,
    doc_date: doc.doc_date, customer_code: doc.customer_code ?? null,
    customer_name: cust?.name_en ?? doc.customer_name ?? null, customer_vat: vat,
    store_id: b.van.id, store_name: b.van.name_en,
    paid_by: doc.paid || !doc.customer_code ? (b.van.cash_name ?? 'Cash') : null,
    subtotal: doc.subtotal ?? '0.00', vat_amount: doc.vat_amount ?? '0.00',
    total: doc.total ?? '0.00', status: 'POSTED', voucher_id: null, lines,
    qr_data_url: await qrDataUrl(d, doc), qr_missing_reason: null,
    seller: { name: co.legal_name || co.name_en, name_ar: co.name_ar,
              vat_number: co.vat_number, cr_number: co.cr_number,
              address: co.address_en, address_ar: co.address_ar, phone: co.phone },
    created_at: doc.issued_at,
  } as unknown as SaleDetail
}

const esc = (v: unknown) => String(v ?? '').replace(/[&<>"]/g, (ch) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]!))
const money = (v: string | number) =>
  Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export function collectionHtml(d: Device, doc: VanDoc): string {
  const b = d.bootstrap
  const co = b.company
  const cust = b.customers.find((c) => c.code === doc.customer_code)
  const time = new Date(doc.issued_at).toLocaleTimeString('en-GB',
    { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Riyadh' })
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(doc.doc_no)}</title>
<style>
  @page { size: 80mm auto; margin: 3mm; }
  * { box-sizing: border-box; }
  body { width: 74mm; margin: 0 auto; font: 11px/1.35 'IBM Plex Sans', 'Segoe UI', Tahoma, sans-serif; color: #000; }
  .c { text-align: center; } .ar { direction: rtl; font-family: 'IBM Plex Sans Arabic', Tahoma, sans-serif; }
  h1 { font-size: 13px; margin: 4px 0 0; }
  .t { font-weight: 700; font-size: 12px; margin: 6px 0 2px; border-top: 1px dashed #000;
       border-bottom: 1px dashed #000; padding: 4px 0; }
  .row { display: flex; justify-content: space-between; gap: 6px; }
  .amt { font-size: 16px; font-weight: 700; border-top: 1px solid #000; margin-top: 6px; padding-top: 4px; }
  .sig { margin-top: 18px; display: flex; gap: 8px; }
  .sig div { flex: 1; border-top: 1px solid #000; padding-top: 2px; text-align: center; font-size: 10px; }
</style></head><body>
  <div class="c">
    <h1>${esc(co.legal_name || co.name_en)}</h1>
    ${co.name_ar ? `<div class="ar">${esc(co.name_ar)}</div>` : ''}
    ${co.vat_number ? `<div>VAT No / الرقم الضريبي: <b>${esc(co.vat_number)}</b></div>` : ''}
    <div class="t">Cash receipt<div class="ar">سند قبض</div></div>
  </div>
  <div class="row"><span>No / رقم</span><b>${esc(doc.doc_no)}</b></div>
  <div class="row"><span>Date / التاريخ</span><span>${esc(doc.doc_date)} ${esc(time)}</span></div>
  <div class="row"><span>From / من</span><span>${esc(cust?.name_en ?? doc.customer_code)}</span></div>
  ${cust?.name_ar ? `<div class="ar" style="text-align:left">${esc(cust.name_ar)}</div>` : ''}
  <div class="row"><span>Salesman / المندوب</span><span>${esc(b.van.salesman)}</span></div>
  ${doc.notes ? `<div>${esc(doc.notes)}</div>` : ''}
  <div class="row amt"><span>Received / المستلم (SAR)</span><span>${money(doc.amount ?? 0)}</span></div>
  <div class="sig"><div>Received by · المستلم</div><div>Customer · العميل</div></div>
  <div class="c" style="margin-top:6px">Thank you · شكراً لكم</div>
</body></html>`
}

export async function ticketFor(d: Device, doc: VanDoc): Promise<Block[]> {
  return doc.kind === 'RECEIPT' ? collectionTicket(d, doc)
    : saleTicket(d, doc, await qrDataUrl(d, doc))
}

/** Print a document the way this phone prints: Bluetooth, or the dialog. */
export async function printDoc(d: Device, doc: VanDoc): Promise<void> {
  const p = await loadPrinter()
  if (p.mode === 'bluetooth') {
    await printBlocks(await ticketFor(d, doc), p)
    return
  }
  if (doc.kind === 'RECEIPT') printHtml(collectionHtml(d, doc))
  else printHtml(receiptHtml(await saleDetailOf(d, doc)))
}
